import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { finalize, Subject, takeUntil } from 'rxjs';
import { LoaderService } from '../../../_core/services/loader.service';
import { ToastService } from '../../../_core/services/toast.service';
import { User, TeamAdmin } from '../../../_shared/models/user.model';
import { TeamDetailsService } from '../team-details.service';
import { Season } from '../../../_shared/models/season.model';
import { SeasonSelectorService } from '../../../_shared/components/season-selector/season-selector.service';
import { ModalController } from '@ionic/angular';
import { ConfirmEmailComponent } from '../confirm-email/confirm-email.component';
import { VerifyEmailComponent } from '../verify-email/verify-email.component';
import { Router } from '@angular/router';
import { Team } from '../../../_shared/models/team.model';
import { PopupCardComponent } from '../../../_shared/components/popup-card/popup-card.component';
import { UserService } from '../../../_core/services/user.service';
import { AuthService } from '../../../auth/auth.service';
import { TeamDetailsComponent } from '../team-details.component';
import { images } from '../../../../app/_shared/data/image';

@Component({
  standalone: false,
  selector: 'app-team-admins',
  templateUrl: './team-admins.component.html',
  styleUrls: ['./team-admins.component.scss'],
})
export class TeamAdminsComponent implements OnInit, OnDestroy {
  teamAdmins: TeamAdmin;
  isLoading: boolean = true;
  selectedSeason: Season;

  private readonly destroy$: Subject<void> = new Subject();
  teamId: string;
  removingAdmin: boolean;
  private modalController = inject(ModalController);
  isOwner: boolean;
  team: Team;
  authUser: User | undefined;
  avatarImage = images.avatarImage;

  constructor(
    private seasonSelectorService: SeasonSelectorService,
    private teamDetailsService: TeamDetailsService,
    private toastService: ToastService,
    private loaderService: LoaderService,
    private userService: UserService,
    private authService: AuthService,
    private router: Router,
    private teamDetailsComponent: TeamDetailsComponent
  ) {

    const nav = this.router.getCurrentNavigation();
    if (nav?.extras.state?.['team']) {
      this.team = nav.extras.state['team'];
    }
    this.isOwner = this.teamDetailsService.isOwner(this.team.owners);;
  }

  ngOnInit() {
    const urlSplit = this.router.url.split('/');
    this.teamId = this.teamDetailsService?.teamInfo?._id ?? urlSplit[urlSplit.length - 2];
    this.selectedSeason = this.seasonSelectorService.season;
    this.getTeamAdmins();
    this.subscriptions();
    this.getMyProfile();
    this.teamDetailsComponent.staffState.subscribe(() => {
      this.getTeamAdmins();
    });
  }

  async getTeamInfo() {
    this.isLoading = true;
    this.team = await this.teamDetailsService.info(this.teamId);
    this.isOwner = this.teamDetailsService.isOwner(this.team.owners);
  }

  hasPendingRequests(): boolean {
    return this.teamAdmins?.coaches?.some(c => c.status === 'pending') || false;
  }

  getPendingRequestsCount(): number {
    return this.teamAdmins?.coaches?.filter(c => c.status === 'pending').length;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }


  removeAdmin(index: number) {
    this.loaderService.present();
    this.removingAdmin = true;

    this.teamDetailsService
      .removeOwner(this.teamId, this.teamAdmins.owners[index]?._id ?? '')
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loaderService.dismiss();
          this.removingAdmin = false
        })
      )
      .subscribe({
        next: res => {
          this.teamAdmins.owners.splice(index, 1);
          this.toastService.presentToast(res.message);
        }
      })
  }

  private subscriptions() {
    this.teamDetailsService
      .subscribeAddOwner()
      .pipe(
        takeUntil(this.destroy$),
      )
      .subscribe(
        owners => this.teamAdmins.owners = owners
      )
  }


  getTeamAdmins() {
    this.getTeamInfo();
    this.isLoading = true;
    //const teamId = this.teamDetailsService?.teamInfo?._id;
    if (this.teamId) {
      this.teamDetailsService
        .getOwners(this.teamId)
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => this.isLoading = false))
        .subscribe({
          next: async (res) => {
            this.isLoading = false;
            if (res?.data) {
              this.teamAdmins = res.data;
            }
            //await this.getTeamOwnerStatus();
          }
        });
    }
  }


  async claimAdmin(index: number) {
    const modal = await this.modalController.create({
      component: ConfirmEmailComponent,
      componentProps: {
        displayEmail: this.teamAdmins.coaches[index]?.email,
        userId: this.teamAdmins.coaches[index]?._id,
        teamId: this.teamId
      }
    });

    modal.onDidDismiss().then((result) => {
      if (result.data) {
        if (result.data?.confirmed) {
          this.getTeamAdmins();
        }
      }
    });

    await modal.present();

    // const { data } = await modal.onDidDismiss();

    //this.official = data;
  }

  async approveAdmin(index: number) {
    const modal = await this.modalController.create({
      component: PopupCardComponent,
      componentProps: {
        message: `${this.teamAdmins?.coaches[index]?.first_name ?? ''} ${this.teamAdmins?.coaches[index]?.last_name ?? ''} (${this.teamAdmins?.coaches[index]?.phone ?? ''}, ${this.teamAdmins?.coaches[index]?.email ?? ''}) requests to claim the profile “${this.teamAdmins?.coaches[index]?.first_name ?? ''} ${this.teamAdmins?.coaches[index]?.last_name ?? ''}” Approve to merge all match results under their profile.`,
        primaryLabel: 'Yes, approve',
        secondaryLabel: 'No, decline'
      },
      cssClass: 'dialog-popover',
      showBackdrop: false,   // 🚀 removes the dark backdrop
      backdropDismiss: false // (optional) prevent click outside
    });

    modal.onDidDismiss().then((result) => {
      if (result.data) {
        this.approveOrRejectAdminRequest(this.teamAdmins?.coaches[index]._id ?? '', result.data?.success ? 'verified' : 'declined');
      }
    });

    await modal.present();
  }

  approveOrRejectAdminRequest(userId: string, status: string) {
    this.isLoading = true;
    this.teamDetailsService.approveOrRejectStaffRequest(userId, this.teamId, this.selectedSeason?._id ?? '', 'coach', status)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.isLoading = false))
      .subscribe({
        next: async (res) => {
          this.isLoading = false;
          this.getTeamAdmins();
        }
      });
  }

  revokeAdminRequest() {
    this.isLoading = true;
    this.teamDetailsService.approveOrRejectStaffRequest(this.authUser?._id ?? '', this.teamId, this.selectedSeason?._id ?? '', 'coach', 'revoked')
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.isLoading = false))
      .subscribe({
        next: async (res) => {
          this.isLoading = false;
          this.getTeamAdmins();
        }
      });
  }

  async verifyEmail(email: string) {
    const modal = await this.modalController.create({
      component: VerifyEmailComponent,
      componentProps: {
        email: email
      }
    });

    modal.onDidDismiss().then((result) => {
      if (result.data) {
        if (result.data?.confirmed) {
          this.getTeamAdmins();
        }
      }
    });

    await modal.present();

    // const { data } = await modal.onDidDismiss();

    //this.official = data;
  }

  async claimCoach(email: string) {
    if (this.teamId) {
      this.teamDetailsService
        .startClaimCoach(this.teamId, email)
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => this.isLoading = false))
        .subscribe({
          next: async (res) => {
            this.isLoading = false;
            // if (res?.data) {
            await this.verifyEmail(email);
            // }
          }
        });
    }
  }

  async verifyClaimCoach(code: string) {
    if (this.teamId) {
      this.teamDetailsService
        .verifyClaimCoach(this.teamId, code)
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => this.isLoading = false))
        .subscribe({
          next: async (res) => {
            this.isLoading = false;
            if (res) {
              this.getTeamAdmins();
            }
          }
        });
    }
  }

  private getMyProfile() {
    this.userService.myProfile().subscribe(
      (responseSuccess) => {
        this.authUser = responseSuccess.data;
      }
    );
    this.authService.authUser$.subscribe(user => {
      this.authUser = user;
    })
  }

  async displayStaffJoin() {
    const modal = await this.modalController.create({
      component: ConfirmEmailComponent,
      componentProps: {
        displayEmail: `You must be current staff of this school.\n All staff members will receive notification that you joined the team.\n\n You must enter .edu address.`,
        teamId: this.teamId
      },
      presentingElement: await this.modalController.getTop(),
    });

    modal.onDidDismiss().then((result) => {
      if (result.data) {
        if (result.data?.confirmed) {
          this.getTeamInfo();
          this.getTeamAdmins();
          this.teamDetailsService.publishUpdateStaff(true);
        }
      }
    });

    await modal.present();

  }
}
